import { Injectable, Injector } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { Money, Ticket, OrderSaveResult, VuState, PrintTaskResult, DtoVuConfiguration, Configuration } from '../../../lib/lib';
import { IVuConnection, IVuConnectionSimulator } from './vu-connection.interfaces';
import { LoggingService } from '../../logging/logging.service';
import { ILog } from '../../logging/log.interface';
import { CardDispenserActionResult } from '../../../lib/rfid-card/card-dispenser-action-result';
import { LanguageService } from '../../language.service';
import { CreditCardTerminalEvent } from '../../../lib/credit-card/credit-card-terminal-event';
import { CIOBoardEvent } from '../../../lib/cioboard/coiboard-event';

@Injectable()
export abstract class VuConnectionBase implements IVuConnection, IVuConnectionSimulator {
  navigationUrlChanged: EventEmitter<string> = new EventEmitter(true);
  eventConnectionChanged: EventEmitter<boolean> = new EventEmitter(true);
  eventBarcodeRead = new EventEmitter<string>(true);
  eventRfidCardRead = new EventEmitter<string>(true);
  eventMoneyChanged = new EventEmitter<Money>(true);
  eventReturnAmountFinished = new EventEmitter<any>();
  eventReturnChangeFinished = new EventEmitter<any>();
  eventOrderSaveResultReceived = new EventEmitter<OrderSaveResult>(true);
  eventPrintTaskResultReceived = new EventEmitter<PrintTaskResult>(true);
  eventPrintTicketRemoved = new EventEmitter<PrintTaskResult>(true);
  eventVuStateChanged = new EventEmitter<VuState>(true);
  eventVuConfigurationChanged = new EventEmitter<DtoVuConfiguration>(true);
  eventOnTurnstileEnter = new EventEmitter<any>();
  eventOnTicketRemoved = new EventEmitter<any>();
  eventOnInvalidRfidCardInserted = new EventEmitter<any>();
  eventCreditCardTerminalEvent = new EventEmitter<CreditCardTerminalEvent>();
  eventGateTransactionBegin = new EventEmitter<boolean>();
  eventGateTransactionEnd = new EventEmitter<boolean>();
  eventBnaValidationStarted = new EventEmitter<any>();
  eventBnaValidationFinished = new EventEmitter<any>();
  eventCIOBoardStateChanged = new EventEmitter<CIOBoardEvent>();
  eventRfidCardProduced = new EventEmitter<CardDispenserActionResult>(true);
  eventRfidCardReleased = new EventEmitter<CardDispenserActionResult>(true);
  eventRfidCardTaken = new EventEmitter<CardDispenserActionResult>(true);
  eventRfidCardCaptured = new EventEmitter<CardDispenserActionResult>(true);
  eventRfidCardRemoved = new EventEmitter<CardDispenserActionResult>(true);
  eventRfidCardTransactionStarted = new EventEmitter<CardDispenserActionResult>(true);
  eventRfidCardAvailabilityChanged = new EventEmitter<CardDispenserActionResult>(true);
  eventMoneyExchangePayin: EventEmitter<Money> = new EventEmitter<Money>(true);
  eventMoneyExchangePayout: EventEmitter<Money> = new EventEmitter<Money>(true);
  eventMoneyExchangeReadyToMoneyExchange: EventEmitter<any> = new EventEmitter<any>(true);

  isConneсted: boolean;
  private _loggingService: LoggingService;
  private languageService: LanguageService;

  constructor(
    protected injector: Injector,
  ) {
    this.init();
  }

  protected init() {
    this.languageService = this.injector.get(LanguageService);
  }

  protected get log(): ILog {
    if (this._loggingService == null) {
      this._loggingService = this.injector.get(LoggingService);
    }
    return this._loggingService;
  }

  barcodeRead(barcode: string) {
    this.log.info(`${this.constructor.name}. readBarcode. ${barcode}`);
    this.eventBarcodeRead.emit(barcode);
  };

  rfidCardRead(rfidCardCode: string) {
    this.log.info(`${this.constructor.name}. readRfidCard. ${rfidCardCode}`);
    this.eventRfidCardRead.emit(rfidCardCode);
  };

  scanTicket(ticket: Ticket) {
    this.log.info(`${this.constructor.name}. scanTicket. ${ticket}`);
  };

  moneyChanged(cash: Money) {
    this.log.info(`${this.constructor.name}. moneyChanged. ${cash}`);
  };

  returnAmountFinished() {
    this.log.info(`${this.constructor.name}. returnAmountFinished.`);
    this.eventReturnAmountFinished.emit();
  }

  returnChangeFinished() {
    this.log.info(`${this.constructor.name}. returnChangeFinished.`);
    this.eventReturnChangeFinished.emit();
  }

  orderSaveResultReceived(result: OrderSaveResult) {
    this.log.info(`${this.constructor.name}. orderSaveResultReceived. ${result}`);
  };

  printTaskResultReceived(result: PrintTaskResult) {
    this.log.info(`${this.constructor.name}. printTaskResultReceived. ${result}`);
  }

  printTicketRemoved(result: PrintTaskResult) {
    this.log.info(`${this.constructor.name}. printTicketRemoved. ${result}`);
  }

  printReceiptResultReceived(result: boolean) {
    this.log.info(`${this.constructor.name}. printTicketsResultReceived. ${result}`);
  }

  connectionChanged(isConnected: boolean) {
    this.eventConnectionChanged.emit(isConnected);
    this.log.info(`${this.constructor.name}. connectionChanged. ${isConnected}`);
  }

  vuConfigurationChanged() {
    this.log.info(`${this.constructor.name}. vuConfigurationChanged.`);
    this.eventVuConfigurationChanged.emit();
  }

  configurationChanged(configuration: Configuration) {
    const dtoVuConfiguration = DtoVuConfiguration.fromOther(configuration);
    dtoVuConfiguration.locale = this.languageService.defaultLanguageId;
    this.vuConfigurationChanged();
  }

  isUseProductionApi(x: boolean) {
  }

  onTurnstileEnter() {
    this.log.info(`${this.constructor.name}. onTurnstileEnter.`);
    this.eventOnTurnstileEnter.emit();
  }

  rfidCardProduced(result: CardDispenserActionResult) {
    let actionResult = CardDispenserActionResult.fromJson(result);
    this.log.info(`${this.constructor.name}. rfidCardProduced. ${actionResult}`);
    this.eventRfidCardProduced.emit(actionResult);
  }

  rfidCardReleased(result: CardDispenserActionResult) {
    let actionResult = CardDispenserActionResult.fromJson(result);
    this.log.info(`${this.constructor.name}. rfidCardReleased. ${actionResult}`);
    this.eventRfidCardReleased.emit(actionResult);
  }

  rfidCardTaken(result: CardDispenserActionResult) {
    let actionResult = CardDispenserActionResult.fromJson(result);
    this.log.info(`${this.constructor.name}. rfidCardTaken. ${actionResult}`);
    this.eventRfidCardTaken.emit(actionResult);
  }

  rfidCardCaptured(result: CardDispenserActionResult) {
    let actionResult = CardDispenserActionResult.fromJson(result);
    this.log.info(`${this.constructor.name}. rfidCardCaptured. ${actionResult}`);
    this.eventRfidCardCaptured.emit(actionResult);
  }

  rfidCardRemoved(result: CardDispenserActionResult) {
    let actionResult = CardDispenserActionResult.fromJson(result);
    this.log.info(`${this.constructor.name}. rfidCardRemoved. ${actionResult}`);
    this.eventRfidCardRemoved.emit(actionResult);
  }

  rfidCardTransactionStarted(result: CardDispenserActionResult) {
    let actionResult = CardDispenserActionResult.fromJson(result);
    this.log.info(`${this.constructor.name}. rfidCardTransactionStarted. ${actionResult}`);
    this.eventRfidCardTransactionStarted.emit(actionResult);
  }

  rfidCardAvailabilityChanged(result: CardDispenserActionResult) {
    let actionResult = CardDispenserActionResult.fromJson(result);
    this.log.info(`${this.constructor.name}. rfidCardAvailabilityChanged. ${actionResult}`);
    this.eventRfidCardAvailabilityChanged.emit(actionResult);
  }

  onTicketRemoved() {
    this.log.info(`${this.constructor.name}. onTicketRemoved.`);
    this.eventOnTicketRemoved.emit();
  }

  onInvalidRfidCardInserted() {
    this.log.info(`${this.constructor.name}. onInvalidRfidCardInserted.`);
    this.eventOnInvalidRfidCardInserted.emit();
  }

  onCreditCardTerminalEvent(x: CreditCardTerminalEvent) {
    this.log.info(`${this.constructor.name}. onCreditCardTerminalEvent(). ${x}`);
    this.eventCreditCardTerminalEvent.emit(x);
  }

  onGateTransactionBegin(x: boolean) {
    this.log.info(`${this.constructor.name}. onEventGateTransactionBegin(). ${x}`);
    this.eventGateTransactionBegin.emit(x);
  }

  onGateTransactionEnd(x: boolean) {
    this.log.info(`${this.constructor.name}. onEventGateTransactionEnd(). ${x}`);
    this.eventGateTransactionEnd.emit(x);
  }

  onBnaValidationStarted() {
    this.log.info(`${this.constructor.name}. onBnaValidationStarted().`);
    this.eventBnaValidationStarted.emit();
  }

  onBnaValidationFinished() {
    this.log.info(`${this.constructor.name}. onBnaValidationFinished().`);
    this.eventBnaValidationFinished.emit();
  }

  onCIOBoardStateChanged(result: CIOBoardEvent) {
    this.log.info(`${this.constructor.name}. onCIOBoardStateChanged().`);
    this.eventCIOBoardStateChanged.emit(result);
  }

  moneyExchangeReadyToMoneyExchange() {
    this.eventMoneyExchangeReadyToMoneyExchange.emit();
    this.log.info(`moneyExchangeReadyToMoneyExchange event`);
  }
}
